import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { bulkCreateProducts } from '../services/ProductServices';
import * as XLSX from 'xlsx';
import { Card, Button, Typography, Space, Upload, message as antdMessage } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import '../assets/styles/AddProduct.css';

export default function BulkProducts() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [statusText, setStatusText] = useState('');

  const handleDownloadTemplate = () => {
    // Encabezados solicitados
    const headers = [
      'Nombre',
      'Categoria',
      'Descripcion',
      'Uso',
      'Medidas (Separadas por ,)',
      'Materiales (Separados por ,)',
      'Certificaciones (Separadas por ,)'
    ];

    // Fila de ejemplo
    const example = [
      'Bomba Centrifuga 1HP',
      'Bombas',
      'Bomba de alta eficiencia para riego',
      'Riego agricola',
      '10cm,20cm,30cm',
      'Acero inoxidable,Plastico',
      'ISO 9001,CE'
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers, example]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'productos_template.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) {
      setStatusText('Selecciona un archivo .xlsx primero.');
      return;
    }
    try {
      setUploading(true);
      setStatusText('');
      const token = await getToken();
      if (!token) throw new Error('No se pudo obtener el token de autenticación.');
      const resp = await bulkCreateProducts(token, file);
      
      // El backend devuelve: { total_rows, created, failed, products, errors }
      const totalRows = resp?.total_rows || 0;
      const created = resp?.created || 0;
      const failed = resp?.failed || 0;
      const errors = resp?.errors || [];
      
      let statusMessage = `Procesadas ${totalRows} filas. `;
      statusMessage += `✅ ${created} productos creados exitosamente. `;
      
      if (failed > 0) {
        statusMessage += `❌ ${failed} productos fallaron. `;
        if (errors.length > 0) {
          statusMessage += `\n\nErrores encontrados:\n`;
          errors.slice(0, 10).forEach(err => {
            statusMessage += `• Fila ${err.row}: ${err.error}\n`;
          });
          if (errors.length > 10) {
            statusMessage += `\n... y ${errors.length - 10} errores más.`;
          }
        }
        antdMessage.warning(`Carga completada: ${created} exitosos, ${failed} con errores`);
        // Redirigir después de 3 segundos incluso si hay errores
        setTimeout(() => {
          navigate('/myproducts');
        }, 3000);
      } else {
        antdMessage.success(`¡Todos los productos se cargaron correctamente! (${created} productos)`);
        // Redirigir después de 2 segundos si todo fue exitoso
        setTimeout(() => {
          navigate('/myproducts');
        }, 2000);
      }
      
      setStatusText(statusMessage);
    } catch (err) {
      console.error(err);
      setStatusText(err?.message || 'Error al subir el archivo.');
      antdMessage.error(err?.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="add-product-page" style={{ maxWidth: 960 }}>
      <Typography.Title level={2} style={{ textAlign: 'left' }}>Carga masiva de productos</Typography.Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Typography.Title level={4}>1. Descargar plantilla (.xlsx)</Typography.Title>
          <Typography.Paragraph type="secondary">
            Descarga la plantilla, completa los campos y vuelve para subirla. Para <b>Medidas</b>, <b>Materiales</b> y <b>Certificaciones</b>
            {' '}usa coma "," para separar valores.
          </Typography.Paragraph>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
            Descargar plantilla
          </Button>
        </Card>

        <Card>
          <Typography.Title level={4}>2. Subir archivo completado</Typography.Title>
          <Upload.Dragger
            multiple={false}
            maxCount={1}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            beforeUpload={(f) => {
              setFile(f);
              setStatusText('Archivo seleccionado: ' + f.name);
              antdMessage.success(`Archivo seleccionado: ${f.name}`);
              return false; // Evita subida automática
            }}
            onRemove={() => {
              setFile(null);
              setStatusText('');
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Haz clic o arrastra el archivo a esta área</p>
            <p className="ant-upload-hint">Solo se admite un archivo .xlsx</p>
          </Upload.Dragger>

          <div style={{ marginTop: 16 }}>
            <Button type="primary" icon={<UploadOutlined />} onClick={handleUpload} loading={uploading} disabled={!file}>
              {uploading ? 'Subiendo...' : 'Subir y procesar'}
            </Button>
          </div>

          {statusText && (
            <Typography.Paragraph 
              style={{ 
                marginTop: 12, 
                whiteSpace: 'pre-line',
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}
            >
              {statusText}
            </Typography.Paragraph>
          )}
        </Card>
      </Space>
    </div>
  );
}
